import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { getPasswordStrength } from "../utils/passwordStrength";
import "../assets/css/login.css";

const initializeTheme = () => {
  const savedTheme = Cookies.get("isDarkMode");
  if (savedTheme !== undefined) {
    return savedTheme === "true";
  }
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
};

function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true, false
  const tokenResolvedRef = useRef(false);
  const [isDarkMode] = useState(initializeTheme());
  const navigate = useNavigate();

  const passwordStrength = getPasswordStrength(newPassword);

  // Apply theme
  useEffect(() => {
    document.body.setAttribute("data-theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Check if user has a valid recovery session
  // Handles both PKCE flow (?code=...) and implicit flow (#access_token=...)
  useEffect(() => {
    let isMounted = true;

    const handleRecovery = async () => {
      // 1. Check for PKCE code in URL query params
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (code) {
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (isMounted) {
            tokenResolvedRef.current = true;
            if (error) {
              console.error("[ResetPassword] Code exchange failed:", error.message);
              setTokenValid(false);
            } else {
              setTokenValid(true);
            }
          }
          // Clean up URL params
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
          console.error("[ResetPassword] Code exchange error:", err);
          if (isMounted) setTokenValid(false);
        }
        return;
      }

      // 2. Check for existing session (implicit flow - hash already processed)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session && isMounted) {
        tokenResolvedRef.current = true;
        setTokenValid(true);
        return;
      }

      // 3. Hash fragment might still be processing — listen for auth state change
      // The supabase client auto-detects #access_token=... in the URL
    };

    handleRecovery();

    // Listen for PASSWORD_RECOVERY or SIGNED_IN events from hash processing
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        tokenResolvedRef.current = true;
        setTokenValid(true);
      }
    });

    // Fallback timeout — if nothing fires after 5s, mark as invalid
    const timeout = setTimeout(() => {
      if (isMounted && !tokenResolvedRef.current) {
        setTokenValid(false);
      }
    }, 5000);

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-hide error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message || "Failed to update password. Please try again.");
        setIsLoading(false);
        return;
      }

      setSuccess(true);

      // Sign out and redirect to login after a brief delay
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate("/login");
      }, 2500);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while checking token
  if (tokenValid === null) {
    return (
      <div className="login-container-bg">
        <div className="login-forgot-wrapper">
          <div className="login-card-custom login-form-animated">
            <div className="login-forgot-header">
              <div className="login-forgot-icon">
                <i className="bi bi-arrow-repeat"></i>
              </div>
              <h2 className="login-forgot-title">Verifying...</h2>
              <p className="login-forgot-subtitle">
                Please wait while we verify your reset link
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid/expired token
  if (tokenValid === false) {
    return (
      <div className="login-container-bg">
        <div className="login-forgot-wrapper">
          <div className="login-card-custom login-form-animated">
            <div className="login-forgot-header">
              <div className="login-forgot-icon" style={{ background: "linear-gradient(135deg, var(--danger) 0%, #e0001b 100%)" }}>
                <i className="bi bi-exclamation-triangle"></i>
              </div>
            </div>

            <div className="login-error-state">
              <h3>Link expired or invalid</h3>
              <p>
                This password reset link has expired or has already been used.
                Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="login-btn"
                style={{
                  textAlign: "center",
                  display: "block",
                  textDecoration: "none",
                }}
              >
                Request a New Link
              </Link>
            </div>

            <div className="login-back-link">
              <Link to="/login">
                <i className="bi bi-arrow-left"></i> Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-container-bg">
        <div className="login-forgot-wrapper">
          <div className="login-card-custom login-form-animated">
            <div className="login-forgot-header">
              <div className="login-forgot-icon">
                <i className="bi bi-check-lg"></i>
              </div>
            </div>

            <div className="login-success-state">
              <h3>Password updated!</h3>
              <p>
                Your password has been changed successfully. Redirecting you to
                sign in...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="login-container-bg">
      <div className="login-forgot-wrapper">
        <div className="login-card-custom login-form-animated">
          {/* Header */}
          <div className="login-forgot-header">
            <div className="login-forgot-icon">
              <i className="bi bi-shield-lock"></i>
            </div>
            <h2 className="login-forgot-title">Set new password</h2>
            <p className="login-forgot-subtitle">
              Create a strong password for your account
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="login-error-alert">
                <i className="bi bi-exclamation-circle"></i>
                {error}
              </div>
            )}

            {/* New Password */}
            <div className="login-form-group">
              <label htmlFor="new-password">New Password</label>
              <div className="login-password-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control login-input-custom"
                  id="new-password"
                  value={newPassword}
                  placeholder="Enter your new password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="login-password-btn"
                >
                  <i
                    className={`bi ${
                      showNewPassword ? "bi-eye-slash" : "bi-eye"
                    }`}
                  ></i>
                </button>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="login-password-strength">
                  <div className="login-strength-bars">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`login-strength-bar ${
                          level <= passwordStrength.strength ? "active" : ""
                        }`}
                        style={{
                          "--strength-color": passwordStrength.color,
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="login-strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="login-form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <div className="login-password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control login-input-custom"
                  id="confirm-password"
                  value={confirmPassword}
                  placeholder="Confirm your new password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    borderColor:
                      confirmPassword && newPassword !== confirmPassword
                        ? "var(--danger)"
                        : confirmPassword && newPassword === confirmPassword
                        ? "var(--teal-bg)"
                        : undefined,
                  }}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="login-password-btn"
                >
                  <i
                    className={`bi ${
                      showConfirmPassword ? "bi-eye-slash" : "bi-eye"
                    }`}
                  ></i>
                </button>
              </div>

              {/* Match indicator */}
              {confirmPassword && newPassword !== confirmPassword && (
                <div className="login-match-indicator error">
                  <i className="bi bi-x-circle"></i>
                  Passwords do not match
                </div>
              )}
              {confirmPassword && newPassword === confirmPassword && (
                <div className="login-match-indicator success">
                  <i className="bi bi-check-circle"></i>
                  Passwords match
                </div>
              )}
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: "8px" }}
                  ></span>
                  Updating...
                </>
              ) : (
                "Update Password"
              )}
            </button>
          </form>

          <div className="login-back-link">
            <Link to="/login">
              <i className="bi bi-arrow-left"></i> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
